import React from 'react';
import { Star } from 'lucide-react';
import { wallOfLove } from '../../testimonials/testimonials';
import type { Testimonial } from '../../testimonials/testimonials';

const HERO_TESTIMONIAL_IDS = [
  36, 34, 33, 35, 22, 16, 12, 20, 21, 38, 25, 26, 32, 13, 23, 37,
];

const ribbonQuote = (testimonial: Testimonial) => {
  const highlighted = testimonial.highlights?.find(text => text.length > 8);
  const quote = highlighted || testimonial.content.split(/(?<=[.!?])\s+/)[0] || testimonial.content;

  return quote.length > 88 ? `${quote.slice(0, 88)}...` : quote;
};

const firstName = (name: string) => name.split(' ')[0] || name;

const HeroTestimonials: React.FC = () => {
  const testimonials = HERO_TESTIMONIAL_IDS
    .map(id => wallOfLove.find(testimonial => testimonial.id === id && testimonial.image))
    .filter((testimonial): testimonial is Testimonial => Boolean(testimonial));

  const renderRibbon = (items: Testimonial[], prefix: string, rotations: number[]) => (
    <>
      {[...items, ...items].map((testimonial, index) => {
        const itemIndex = index % items.length;
        const rotation = rotations[itemIndex % rotations.length];
        const accentColor = itemIndex % 3 === 0
          ? 'var(--vi-accent-primary)'
          : itemIndex % 3 === 1
            ? 'var(--vi-accent-support)'
            : 'var(--vi-accent-highlight-strong)';

        return (
          <article
            key={`${prefix}-${testimonial.id}-${index}`}
            className="hero-testimonial-polaroid"
            style={{ transform: `rotate(${rotation}deg)` }}
          >
            <span className="hero-testimonial-tape" />
            <div className="hero-testimonial-photo">
              <img
                src={testimonial.image || ''}
                alt={testimonial.name}
                loading="lazy"
                onError={event => {
                  event.currentTarget.style.display = 'none';
                }}
              />
              <div className="hero-testimonial-stars" aria-hidden="true">
                {Array.from({ length: 5 }).map((_, starIndex) => (
                  <Star key={starIndex} className="hero-testimonial-star" />
                ))}
              </div>
            </div>
            <div className="hero-testimonial-caption">
              <p>"{ribbonQuote(testimonial)}"</p>
              <span style={{ color: accentColor }}>— {firstName(testimonial.name)}</span>
            </div>
          </article>
        );
      })}
    </>
  );

  return (
    <div className="hero-testimonial-ribbons" aria-label="VoiceInk customer testimonials">
      <style>
        {`
          @keyframes hero-testimonial-scroll-left {
            from { transform: translateX(0); }
            to { transform: translateX(-50%); }
          }

          .hero-testimonial-ribbons {
            position: relative;
            margin-inline: calc(50% - 50vw);
            max-width: 100vw;
            overflow: hidden;
            padding: 16px 0 22px;
            box-sizing: border-box;
          }

          .hero-testimonial-row {
            display: flex;
            overflow: hidden;
            padding: 18px 0 24px;
          }

          .hero-testimonial-track {
            display: flex;
            min-width: max-content;
            animation: hero-testimonial-scroll-left 92s linear infinite;
            will-change: transform;
          }

          .hero-testimonial-polaroid {
            position: relative;
            flex: 0 0 auto;
            width: clamp(178px, 18vw, 232px);
            margin-right: 24px;
            padding: 9px 9px 17px;
            box-sizing: border-box;
            background: var(--vi-bg-panel-elevated);
            border: 1px solid rgba(61, 46, 30, 0.1);
            box-shadow:
              0 2px 5px rgba(26, 18, 9, 0.06),
              0 12px 26px rgba(100, 70, 40, 0.12);
            transition:
              transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1),
              box-shadow 0.28s ease;
          }

          .hero-testimonial-polaroid:hover {
            z-index: 3;
            transform: translateY(-5px) rotate(0deg) !important;
            box-shadow:
              0 7px 14px rgba(26, 18, 9, 0.09),
              0 18px 38px rgba(100, 70, 40, 0.18);
          }

          .hero-testimonial-tape {
            position: absolute;
            top: -8px;
            left: 50%;
            width: 54px;
            height: 17px;
            transform: translateX(-50%) rotate(-2deg);
            background: linear-gradient(180deg, rgba(232, 166, 68, 0.52), rgba(196, 99, 63, 0.28));
            box-shadow: 0 1px 2px rgba(26, 18, 9, 0.08);
          }

          .hero-testimonial-photo {
            position: relative;
            aspect-ratio: 1;
            overflow: hidden;
            background: var(--vi-bg-surface-tertiary);
          }

          .hero-testimonial-photo img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
            filter: sepia(0.12) saturate(1.06) contrast(1.02);
          }

          .hero-testimonial-stars {
            align-items: center;
            position: absolute;
            left: 6px;
            bottom: 6px;
            display: inline-flex;
            gap: 1px;
            border-radius: 5px;
            padding: 3px 5px;
            background: rgba(255, 253, 248, 0.92);
            color: var(--vi-accent-highlight);
            line-height: 1;
          }

          .hero-testimonial-star {
            width: 8px;
            height: 8px;
            fill: currentColor;
            stroke-width: 0;
          }

          .hero-testimonial-caption {
            padding: 11px 3px 0;
          }

          .hero-testimonial-caption p {
            display: -webkit-box;
            min-height: 58px;
            margin: 0;
            overflow: hidden;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 3;
            color: var(--vi-text-secondary);
            font-family: 'Lora', Georgia, serif;
            font-size: 13.5px;
            font-style: italic;
            font-weight: 700;
            letter-spacing: -0.015em;
            line-height: 1.42;
            overflow-wrap: anywhere;
          }

          .hero-testimonial-caption span {
            display: block;
            margin-top: 9px;
            font-family: 'Nunito', sans-serif;
            font-size: 11.5px;
            font-weight: 900;
            letter-spacing: 0.1em;
            line-height: 1.1;
            text-transform: uppercase;
            overflow-wrap: anywhere;
          }

          @media (max-width: 640px) {
            .hero-testimonial-ribbons {
              padding-top: 4px;
              padding-bottom: 18px;
            }

            .hero-testimonial-row {
              padding: 14px 0;
            }

            .hero-testimonial-polaroid {
              width: clamp(150px, 52vw, 172px);
              margin-right: clamp(12px, 4vw, 18px);
              padding: 8px 8px 14px;
            }

            .hero-testimonial-caption {
              padding-top: 9px;
            }

            .hero-testimonial-caption p {
              min-height: 52px;
              font-size: 12.5px;
            }
          }

          @media (max-width: 360px) {
            .hero-testimonial-polaroid {
              width: 148px;
            }

            .hero-testimonial-caption p {
              min-height: 50px;
              font-size: 12px;
            }

            .hero-testimonial-caption span {
              font-size: 10.5px;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .hero-testimonial-track {
              animation-duration: 0.01ms;
              animation-iteration-count: 1;
            }
          }
        `}
      </style>

      <div className="hero-testimonial-row">
        <div className="hero-testimonial-track">
          {renderRibbon(testimonials, 'hero-ribbon', [-2, 1.5, -1, 2.2, -2.4, 0.8, -1.6, 2.4, -0.5, 1.2, -2.1, 1.8])}
        </div>
      </div>
    </div>
  );
};

export default HeroTestimonials;
